<template>
    <div class="tab-container project-settings">
      <div class="project-settings__update-loader" v-if="isDataUpdating">
        <base-loader class="project-settings__update-loader-ring"/>
      </div>
      <div class="row section-row main-content-row">
        <div class="section-left">
          <div class="row-label">
            <h2>Your Shop Name</h2>
            <p>This is the main shop all other shops will be compared to</p>
          </div>
        </div>
        <div class="section-right">
          <div class="row">
            <base-smart-input-autocomplete
              easyModel
              ref="dropdownClientShopRef"
              class="select-client-shop"
              :options="shopsDropdown"
              v-model="dropdownClientShop"
              placeholder="Client Shop"
              >
              <base-material-design-icon class="icon-arrow" icon="arrowDown" />
            </base-smart-input-autocomplete>
            <button class="button-update-client-shop"
            @click="updateClientShop"
            >Update</button>
          </div>
        </div>
      </div>
      <div class="row main-content-row">
        <div class="section-left">
          <div class="row-label">
            <h2>Competitor Shops</h2>
            <p>These are the shops that will be compared to your main shop. Select the shops you want to track in your reports.</p>
          </div>
          <div class="shop-listing" v-show="clientShop">
            <div class="client-shop-section">
              <div class="client-shop-section-title">
                <span class="client-shop-section-title-name">{{ clientShop }}</span>
                <p class="client-shop-section-title-desc">Visibility Rank</p>
              </div>
              <div class="client-shop-section-rank">
                <span class="client-shop-section-rank-number">{{ shops.find(shop => shop.shop_name === clientShop)?.visibility_rank || 'N/A' }}</span>
              </div>
            </div>
            <div class="section-row"></div>
            <div class="competitor-shop-section">
              <div class="row space_row competitor-shop-section-header">
                <label class="competitor-shop-section-header-label">Selected Competitors</label>
                <span class="competitor-shop-section-header-count">{{ competitorShops.length }} / {{ competitorLimit }}</span>
              </div>
              <div v-for="shop in competitorShops" 
                @click="deselectCompetitor(shop)"
                class="row space_row competitor-shop-section-item">
                <span>{{ shop }}</span>
                <base-material-design-icon  class="competitor-shop-section-item-close" icon="close" />
              </div>
              <div class="row space_row competitor-shop-section-freetext">
                <input 
                  v-model="freetextCompetitor"
                  @keyup.enter="addFreetextCompetitor"
                  placeholder="Add competitor manually"
                  class="competitor-shop-section-freetext-input"
                  :disabled="competitorShops.length >= competitorLimit"
                />
                <button 
                  @click="addFreetextCompetitor"
                  class="competitor-shop-section-freetext-button"
                  :disabled="!freetextCompetitor.trim() || competitorShops.length >= competitorLimit"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="section-right" v-show="clientShop">
          <div class="row">
            <div class="button-tab section-row section-row-table"
            :class="{ 'button-tab__selected': currentFilter === 'similar'}"
            @click="shopsFilter('similar')">
              <div class="button-tab-inner">Similar Shops</div>
            </div>
            <div class="button-tab section-row section-row-table"
            :class="{ 'button-tab__selected': currentFilter === 'all'}"
            @click="shopsFilter('all')">
              <div class="button-tab-inner">All Shops</div>
            </div>
            <div class="section-row section-filler section-row-table"></div>
            <div class="section-row section-row-table">
              <base-smart-input-search class="input-table-search"
                  :allowFreeText="true"
                  ref="tableSearchInput"
                  @input-text="filterShops($event)"
                  placeholder="Search">
              </base-smart-input-search>
            </div>
          </div>
          <div id="tabulator-all-shops" class="table table-all-shops">
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { mapActions, mapState } from 'vuex'
  import Tabulator from 'tabulator-tables'
  import { cloneDeep } from 'lodash'
  
  
  export default {
    name: 'ShopProperties',
    data () {
      return {
        isDataUpdating: false,
        shopDataSet: false,
        disableButtons: false,
        errorFields: [],
        clientShop: null,
        dropdownClientShop: null,
        competitorShops: [],
        shops: [],
        competitorLimit: 10,
        currentFilter: 'similar',
        table: null,
        freetextCompetitor: '',
      }
    },
    watch: {
      clientId: {
        handler (clientId) {
          clientId && this.table && this.loadData()
          this.similarShopsFilter()
        },
        immediate: true
      },
      competitorShops () {
        this.table.redraw(true)
      },
      shops () {
        this.table.setData(this.shops)
        this.similarShopsFilter()
      },
      clientShop (newValue, oldValue) {
        this.currentFilter = 'similar'
        if (!this.table) return
        this.table.setData(this.shops)
        this.similarShopsFilter()
        if (oldValue) {
          let oldRow = this.table.getRow(oldValue)
          oldRow && oldRow.reformat()
        }
        let newRow = this.table.getRow(newValue)
        newRow && newRow.reformat()
        this.table.options.placeholder.innerHTML = `<span>No similar shops found for ${this.clientShop}</span>`
      }
    },
    computed: {
      isUserSuperAdmin () {
        return this.userRoles.includes('admin')
      },
      shopsDropdown () {
        let shopsDropdown = this.shops
        if (this.dropdownClientShop) {
          shopsDropdown = shopsDropdown.filter(v => v.shop_name?.toLowerCase().includes(this.dropdownClientShop?.toLowerCase()))
        }
        shopsDropdown = shopsDropdown.map(shop => ({label: shop.shop_name, value: shop.shop_name }))
        return shopsDropdown
      },
      tableConfig () {
        const scope = this
        return {
          maxHeight: 500,
          data: [],
          placeholder: `No similar shops found for ${scope.clientShop}`,
          height: 500,
          initialSort:[
              {column:'keyword_count', dir:'desc'},
          ],
          index: 'shop_name',
          rowSelectionChanged: function(data, rows){
            this.getRows().forEach(row => row.reformat())
            
            // Get table-selected competitors
            const tableCompetitors = data.map(shop => shop.shop_name)
            
            // Preserve freetext competitors that aren't in the table
            const freetextCompetitors = scope.competitorShops.filter(competitor => 
              !scope.shops.some(shop => shop.shop_name === competitor)
            )
            
            // Combine table and freetext competitors
            scope.competitorShops = [...tableCompetitors, ...freetextCompetitors]
            
            if (!scope.isDataUpdating) {
              if (!scope.competitorShops.length) {
                scope.updateShopData({deleteCompetitors: true})
              } else {
                scope.updateShopData({competitorShops: scope.competitorShops})
              }
              scope.shopDataSet = true
            }
          },
          selectable: scope.competitorLimit,
          selectableRollingSelection:false, 
          selectableCheck:function(row){
              return scope.clientShop && row.getData().shop_name !== scope.clientShop
          },
          columns: [
            {
              title: 'Shop Name',
              field: 'shop_name',
              widthGrow: 2,
              formatter: function(cell, formatterParams, onRendered){
                const row = cell.getRow();
                const value = cell.getValue();
                const isExcluded = value === scope.clientShop
                const noClientShop = !scope.clientShop;


                const div = document.createElement("div");
                div.style.display = "flex";
                div.style.alignItems = "center";

                if (!isExcluded) {
                    const selectedCount = cell.getTable().getSelectedData().length;
                    const isSelected = row.isSelected();
                    const totalCompetitors = scope.competitorShops.length;
                    const disableCheckbox = noClientShop || (!isSelected && totalCompetitors >= scope.competitorLimit)

                    const checkbox = document.createElement("input");
                    checkbox.type = "checkbox";
                    checkbox.style.marginRight = "8px";
                    checkbox.style.cursor = "pointer";
                    checkbox.className = 'table-select-checkbox'
                    checkbox.checked = isSelected;
                    checkbox.disabled = disableCheckbox;

                    checkbox.addEventListener("click", function(e){
                        e.stopPropagation();
                        if (checkbox.checked) {
                            if (scope.competitorShops.length < scope.competitorLimit) {
                                row.select();
                            } else {
                                checkbox.checked = false;
                            }
                        } else {
                            row.deselect();
                        }
                    });

                    div.appendChild(checkbox);
                } else {
                    const spacer = document.createElement("span");
                    spacer.style.display = "inline-block";
                    spacer.style.width = "16px"
                    spacer.style.marginRight = "8px";
                    div.appendChild(spacer);
                }

                const span = document.createElement("span");
                span.textContent = value;
                div.appendChild(span);

                return div;
              },
              cellClick: function(e, cell){
              }
            },
            {
              title: 'Keyword Count',
              field: 'keyword_count',
              sorter: 'number',
            },
            {
              title: 'Number of Product Cards',
              field: 'product_card_count',
            },
            {
              title: 'Visibility Rank',
              field: 'visibility_rank',
            }
          ],
          layout: 'fitColumns',
          pagination:"local",
          paginationSize: 100,
          tooltipsHeader: true,
          rowFormatter:function(row){
              var data = row.getData();
              if(data.shop_name === scope.clientShop){
                  row.getElement().style.backgroundColor = "#2EC4B626";
              } else {
                row.getElement().style.backgroundColor = '#FFF'
              }
          }
        }
      }
    },
    methods: {
      ...mapActions('projectSettings', ['getShops', 'getClientShopData', 'updateClientShopData']),
      ...mapActions(['getCurrentSession']),
      filterShops (shopFilter) {
        if (shopFilter) {
          this.shopsFilter('all')
        }
        this.table.setFilter('shop_name', 'like', shopFilter);
      },
      shopsFilter(filterType) {
        this.currentFilter = filterType
        if (filterType === 'similar') {
          this.$refs.tableSearchInput.clear()
          this.similarShopsFilter()
        } else if (filterType === 'all') {
          this.filterTableData(this.shops.map(shop => shop.shop_name))
        }
      },
      filterTableData(filters) {
        this.table.setFilter('shop_name', 'in', filters)
      },
      similarShopsFilter () {
        if (!this.table || !this.clientShop) return
        let rows = cloneDeep(this.shops)
        rows = rows.sort((a, b) => b.keyword_count - a.keyword_count)
        let clientShopIndex = rows.findIndex(obj => obj.shop_name === this.clientShop)
        if (clientShopIndex === -1) {
          this.filterTableData([this.clientShop])
          return
        }
        let lowBound = clientShopIndex - 5
        lowBound = lowBound < 0 ? 0 : lowBound
        let highBound = clientShopIndex + 6
        highBound = highBound >= rows.length ? rows.length - 1 : highBound
        rows = rows.slice(lowBound, highBound)
        this.filterTableData(rows.map(shop => shop.shop_name))
      },
      deselectCompetitor (shopName) {
        // Remove from competitorShops array
        const index = this.competitorShops.indexOf(shopName)
        if (index > -1) {
          this.competitorShops.splice(index, 1)
        }
        
        // Try to deselect from table (in case it exists in table)
        if (this.table) {
          this.table.deselectRow(shopName)
        }
        

        
        // Update shop data
        if (!this.isDataUpdating) {
          if (this.competitorShops.length === 0) {
            this.updateShopData({deleteCompetitors: true})
          } else {
            this.updateShopData({competitorShops: this.competitorShops})
          }
          this.shopDataSet = true
        }
      },
      addFreetextCompetitor () {
        const competitorName = this.freetextCompetitor.trim()
        if (!competitorName || this.competitorShops.length >= this.competitorLimit) {
          return
        }
        
        // Check if competitor is already in the list
        if (this.competitorShops.includes(competitorName)) {
          this.freetextCompetitor = ''
          return
        }
        
        // Add to competitor list
        this.competitorShops.push(competitorName)
        
        // Check if this shop exists in the table and select it
        const existingShop = this.shops.find(shop => shop.shop_name === competitorName)
        if (existingShop && this.table) {
          this.table.selectRow(competitorName)
        }
        

        
        // Update shop data
        if (!this.isDataUpdating) {
          this.updateShopData({competitorShops: this.competitorShops})
          this.shopDataSet = true
        }
        
        // Clear the input field
        this.freetextCompetitor = ''
      },
      debouncedUpdateShopData: _.debounce(async (scope, data) => {
        const response = await scope.updateClientShopData({
          clientId: scope.clientId,
          ...data
        })

      }, 250),
      async updateShopData (data) {
        if (this.shopDataSet) {
          return await this.debouncedUpdateShopData(this, data)
        }
      },
      async updateClientShop () {
        const response = await this.updateClientShopData({
          clientId: this.clientId,
          shopName: this.dropdownClientShop
        })
        this.clientShop = this.dropdownClientShop
      },
      async loadData (filterString = null) {
        this.isDataUpdating = true
        const response = await this.getShops({clientId: this.clientId, filterString})
        this.shops = response.shops
        this.clientShop = response.client_shop
        this.$refs.dropdownClientShopRef.text = this.clientShop
        
        // Wait for next tick to ensure table has updated
        await this.$nextTick()
        
        // Set competitor shops from response (includes freetext entries)
        this.competitorShops = response.competitor_shops || []
        
        // Select only the competitors that exist in the table
        const tableCompetitors = response.competitor_shops.filter(competitor => 
          this.shops.some(shop => shop.shop_name === competitor)
        )
        this.table.selectRow(tableCompetitors)
        
        await this.$nextTick()
        this.isDataUpdating = false
      },
    },
    mounted () {
      this.table = new Tabulator('#tabulator-all-shops', this.tableConfig)
      this.loadData()
    }
  }
  </script>
  
  <style scoped lang="scss">
    .client-shop-section-title-name {
      height: 90%;
      font-size: 14px;
      font-weight: 700;
    }
    .client-shop-section-title-desc {
      height: 10%;
    }
    .client-shop-section-title {
      width: 80%;
      height: 100%;
      display: flex;
      flex-direction: column;
      font-size: 12px;
      justify-content: flex-start;
      padding: 2px 0;
    }
    .shop-listing {
      width: 100%;
      height: 800px;
      display: flex;
      flex-direction: column;
      justify-content: space-evenly;
    }
    .heading-client-shop {
      height: 50%;
    }
    .client-shop-section-rank-number {
      align-self: center;
    }
    .client-shop-section-rank {
      display: flex;
      justify-content: center;
      width: 30%;
      height: 80%;
      border-radius: 4px;
      background-color: $rfx-bright-turquoise;
      align-self: center;
      color: #fff;
      text-align: center;
    }
    .client-shop-section {
      padding: 5px;
      background-color: #2EC4B626;
      border-radius: 4px;
      width: 100%;
      height: 5%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
    .competitor-shop-section-header {
      height: 4%;
      font-weight: 700;
      justify-content: space-between;
      margin-bottom: 10px;
    }
    .competitor-shop-section-header-label {
      font-weight: 700;
      align-self: flex-end;
    }
    .competitor-shop-section-header-count {
      font-weight: 400;
      font-size: 10px;
      color: $rfx-light-march;
      align-self: flex-end;
    }
    .competitor-shop-section-item {
      font-weight: 400;
      font-size: 12px;
      margin-bottom: 10px;
      color: $rfx-light-march;
      cursor: pointer;
    }
    .competitor-shop-section-item-close {
      font-size: 12px;
      color: $rfx-light-march;
    }
    .competitor-shop-section {
      padding: 5px;
      width: 100%;
      height: 80%;
    }
    .competitor-shop-section-freetext {
      margin-top: 10px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .competitor-shop-section-freetext-input {
      flex: 1;
      height: 30px;
      padding: 0 8px;
      border: 1px solid $rfx-light-steel;
      border-radius: 4px;
      font-size: 12px;
      outline: none;
      transition: border-color 0.25s ease;
      
      &:focus {
        border-color: $rfx-bright-turquoise;
      }
      
      &:disabled {
        background-color: $rfx-light-steel;
        color: $rfx-light-march;
        cursor: not-allowed;
      }
    }
    .competitor-shop-section-freetext-button {
      height: 30px;
      padding: 0 12px;
      background-color: $rfx-bright-turquoise;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 0.25s ease;
      
      &:hover:not(:disabled) {
        background-color: darken($rfx-bright-turquoise, 10%);
      }
      
      &:disabled {
        background-color: $rfx-light-steel;
        color: $rfx-light-march;
        cursor: not-allowed;
      }
    }

    .button-tab-inner {
      height: 30px;
      display: flex;
      align-items: center;   /* vertical centering */
      justify-content: center; 
      background-color: #fff;
      margin-left: 4px;
      margin-right: 4px;
      border-radius: 4px;
      transition: background-color 0.25s ease;

    }
    .button-tab {
      cursor: pointer;
      width: 200px;
      height: 100%;
      background-color: #fff;
      border: none;
      font-weight: 400;
      color: $rfx-light-march;
      &__selected {
        border-bottom: 2px solid $rfx-bright-turquoise !important;
        font-weight: 700 !important;
        color: $rfx-dark-march !important;
      }
      &:hover .button-tab-inner {
        background-color: $rfx-light-steel;
      }
    }
    .input-table-search {
      height: 30px !important;
      margin-left: auto;
    }
    .button-update-client-shop {
        min-width: 100px;
        margin-left: 10px;
        font-size: 10px;
        font-weight: 900;
        cursor: pointer;
        border-radius: 4px;
        font-size: 14px;
        outline: none;
        border: 2px solid;
        border-color: $rfx-light-steel;
        transition: border-color 0.25s ease;
        &:hover {
          border-color: $rfx-dark-march;
        }
        color: $rfx-dark-march;
        background-color: #fff;
    }
    .select-client-shop {
      width: 50%;
      cursor: pointer;
    }
    .icon-arrow {
      padding-right: 5px;
    }
    .section-left {
      width: 20%;
      display: flex;
      flex-direction: column;
    }
    .section-right {
      width: 75%;
      display: flex;
      flex-direction: column;
    }
    h2 {
      font-size: 16px;
      font-weight: 700;
    }
    p {
      font-size: 12px;
      color: $rfx-light-march;
      font-weight: 400;
    }
    .tab-container {
      background-color: #fff;
      display: flex;
      flex-direction: column;
    }
    .main-content-row {
      justify-content: space-between;
    }
    .row {
      background-color: #fff;
      display: flex;
      flex-direction: row;
    }
    .section-row-table {
      margin-top: 10px !important;
      height: 40px;
    }
    .section-row {
      border-bottom: 2px solid $rfx-light-steel;
      margin-bottom: 20px;
    }
    .section-filler {
      width: 100%;
    }
    .space_row {
      justify-content: space-between;
    }
    .project-settings {
      padding-top: 14px;
      position: relative;
      min-height: 50vh !important;
      padding: 50px 100px;
  
      &__tabs {
        display: flex;
        margin-bottom: -1px;
  
        &_btn {
          margin-left: auto;
        }
        &_tab {
          width: max-content;
          padding: 16px 9px;
          text-align: center;
          font-size: 14px;
          color: $rfx-light-march;
          background-color: transparent;
          border: 1px solid transparent;
          text-decoration: none;
  
          &_error {
            color: $rfx-red;
          }
  
          &_active {
            background-color: #fff;
            color: $rfx-dark-march;
            font-weight: bold;
            border: 1px solid $rfx-light-steel;
            border-bottom: 1px solid #fff;
          }
        }
      }
  
  
  
      &__title-container {
        padding: 14px 0;
        display: flex;
        align-items: center;
      }
  
      &__no-data {
        display: flex;
        height: 300px;
        background-color: #fff;
        border-radius: $rfx-default-border-radius;
        align-items: center;
        justify-content: center;
        font-size: 27px;
        font-weight: 600;
        color: $rfx-medium-grey;
        flex: 1 1;
      }
  
      &__update-loader {
        position: absolute;
        display: flex;
        width: 100%;
        height: 100%;
        justify-content: center;
        background-color: white;
        top: 0;
        opacity: 0.5;
        z-index: 10000;
      }
  
      &__update-loader-ring {
        margin-top: 200px;
      }
  
      &__select {
        padding: 3px 0 3px 25px !important;
        height: 60px !important;
  
        &::v-deep .select__input {
          position: relative;
          left: -22px;
          padding-left: 22px;
        }
  
        &::v-deep .select__input-container {
          background-color: #fff;
          margin-left: -4px;
        }
  
      }
  
      &__field_disabled {
        pointer-events: none !important;
        background-color: $rfx-grey;
        color: $rfx-dark-grey;
      }
  
      &__footer {
        margin-top: 40px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
  
        &__button {
          min-width: 100px;
          padding: 0 25px;
          height: 40px;
          font-size: 10px;
          font-weight: 900;
          cursor: pointer;
          text-transform: uppercase;
          border-radius: 8px;
          outline: none;
          border: none;
          color: #fff;
          background-color: $rfx-dark-march;
        }
      }
    }

    ::v-deep(.table-select-checkbox) {
      appearance: none;
      -webkit-appearance: none;
      width: 20px;
      height: 20px;
      border: 2px solid $rfx-light-steel;
      border-radius: 2px;
      margin-right: 8px;
      position: relative;
      cursor: pointer;
      transition: all 0.2s ease;

      &:checked {
        background-color: #fff;
        border-color: $rfx-light-steel;

        &::after {
          content: '';
          position: absolute;
          left: 5px;
          top: 2px;
          width: 4px;
          height: 8px;
          border: solid $rfx-bright-turquoise;
          border-width: 0 2px 2px 0;
          transform: rotate(45deg);
        }
      }

      &:disabled {
        background-color: $rfx-light-steel;
        border-color: $rfx-light-steel;
        cursor: not-allowed;
      }
    }
  
    /* Remove vertical cell borders */
    ::v-deep(.tabulator .tabulator-cell) {
      border-right: none !important;
    }

    ::v-deep(.tabulator-placeholder) {
      background-color: #2EC4B626 !important;
    }

    /* Remove vertical header borders */
    ::v-deep(.tabulator .tabulator-header .tabulator-col) {
      border-right: none !important;
    }

    ::v-deep(.tabulator-cell) {
      height: 40px !important;
      align-content: center !important;
    }

    /* Clean up last column borders if any */
    ::v-deep(.tabulator-row .tabulator-cell:last-child),
    ::v-deep(.tabulator-header .tabulator-col:last-child) {
      border-right: none !important;
    }
    
    ::v-deep(.tabulator-paginator button.active) {
      color: $rfx-dark-march !important;
      border-bottom: 2px solid !important;
      border-radius: 0 !important;
      border-color: $rfx-bright-turquoise !important;
    }

    ::v-deep(.tabulator-paginator button) {
      border-radius: 4px;
      padding: 4px 8px;
      margin: 0 2px;
      border: none !important;
      background-color: white;
      color: $rfx-dark-march !important;
      transition: all 0.2s;
    }

  </style>